import type { IntlShape } from 'react-intl';

import type { RefKind } from '../../contracts';
import { objectReferenceMetadataMessages as messages } from './object-reference-metadata.messages';

export type ObjectReferenceMetadataField =
  | {
      readonly kind: 'sexAge' | 'lastSeen' | 'duration' | 'lastEdited';
      readonly value?: string | null;
    }
  | {
      readonly kind: 'sessionCount' | 'timesUsed' | 'documentCount';
      readonly count?: number | null;
    }
  | {
      readonly kind: 'patientLink';
      readonly status?: 'linked' | 'unlinked' | null;
      readonly patientName?: string | null;
    }
  | {
      readonly kind: 'owner';
      readonly owner?: 'personal' | 'team' | 'community' | null;
    }
  | { readonly kind: 'source'; readonly source?: 'generatedByHeidi' | null }
  | {
      readonly kind: 'taskStatus';
      readonly status?: 'todo' | 'in_progress' | 'done' | 'archived' | null;
    }
  | {
      readonly kind: 'dueDate';
      readonly value?: string | null;
      readonly overdue?: boolean;
    };

export type ObjectReferenceSummary = {
  readonly kind: RefKind;
  readonly title: string;
  readonly titleDetail?: string;
  readonly avatarSeed?: string;
  readonly summary?: string;
  readonly metadata: readonly ObjectReferenceMetadataField[];
};

type FieldKind = ObjectReferenceMetadataField['kind'];

const METADATA_LIMIT = 3;

// patientLink and lastEdited are shared, and a missing patient leads.
const FIELD_ORDER: readonly FieldKind[] = [
  'patientLink',
  'taskStatus',
  'dueDate',
  'sexAge',
  'lastSeen',
  'sessionCount',
  'duration',
  'source',
  'owner',
  'documentCount',
  'lastEdited',
  'timesUsed',
];

const OWNER_MESSAGES = {
  personal: messages.ownerPersonal,
  team: messages.ownerTeam,
  community: messages.ownerCommunity,
} as const;

const TASK_STATUS_MESSAGES = {
  todo: messages.taskTodo,
  in_progress: messages.taskInProgress,
  done: messages.taskDone,
  archived: messages.taskArchived,
} as const;

const formatMetadataField = (
  field: ObjectReferenceMetadataField,
  kind: ObjectReferenceSummary['kind'],
  intl: IntlShape
): string | null => {
  switch (field.kind) {
    case 'sexAge':
    case 'duration':
      return field.value?.trim() || null;
    case 'lastSeen':
    case 'lastEdited': {
      const date = field.value?.trim();
      return date ? intl.formatMessage(messages[field.kind], { date }) : null;
    }
    case 'sessionCount':
    case 'timesUsed':
    case 'documentCount':
      return field.count === undefined || field.count === null
        ? null
        : intl.formatMessage(messages[field.kind], { count: field.count });
    case 'patientLink': {
      if (field.status === 'unlinked')
        return intl.formatMessage(messages.noPatientLinked);
      const name = field.patientName?.trim();
      return field.status === 'linked' && name
        ? intl.formatMessage(
            kind === 'task' ? messages.taskForPatient : messages.linkedTo,
            { name }
          )
        : null;
    }
    case 'owner':
      return field.owner ? intl.formatMessage(OWNER_MESSAGES[field.owner]) : null;
    case 'source':
      return field.source === 'generatedByHeidi'
        ? intl.formatMessage(messages.sourceGeneratedByHeidi)
        : null;
    case 'taskStatus':
      return field.status ? intl.formatMessage(TASK_STATUS_MESSAGES[field.status]) : null;
    case 'dueDate': {
      if (field.overdue) return intl.formatMessage(messages.overdue);
      const date = field.value?.trim();
      return date ? intl.formatMessage(messages.dueDate, { date }) : null;
    }
  }
};

export const findMetadataField = <K extends FieldKind>(
  object: ObjectReferenceSummary,
  kind: K
): Extract<ObjectReferenceMetadataField, { kind: K }> | undefined =>
  object.metadata.find(
    (field): field is Extract<ObjectReferenceMetadataField, { kind: K }> =>
      field.kind === kind
  );

export const formatObjectReferenceMetadata = (
  object: ObjectReferenceSummary,
  intl: IntlShape
): string[] =>
  [...object.metadata]
    .sort((a, b) => FIELD_ORDER.indexOf(a.kind) - FIELD_ORDER.indexOf(b.kind))
    .flatMap((field) => formatMetadataField(field, object.kind, intl) ?? [])
    .slice(0, METADATA_LIMIT);
